import React, { Component } from 'react'
import type { ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { logger } from '../utils/logger'
import { config } from '../config'
import { errorReporting } from '../services/errorReporting'
import { captureBufferPushError } from '../features/bug-report/captureBuffer'
import { maybeReportAutoBug } from '../features/bug-report/autoReporter'
import PermissionDenied from './PermissionDenied'
import { isChunkLoadError } from '../utils/chunkLoadError'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  location?: { pathname: string } // Optional location prop for route-based reset
}

interface State {
  hasError: boolean
  error?: Error
}

/** Detect HTTP 403 on ApiError-shaped values or matching message hints. Used by
 *  the boundary to render PermissionDenied instead of the generic "something
 *  went wrong" card when a page throws an unhandled 403 from an API call. */
function isForbiddenError(error: Error | undefined): boolean {
  if (!error) return false
  const status = (error as Error & { status?: unknown }).status
  if (status === 403) return true
  const msg = (error.message ?? '').toLowerCase()
  return (
    msg.includes('forbidden') ||
    msg.includes('access denied') ||
    msg.includes('not authorized') ||
    msg.includes('permission denied')
  )
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('ErrorBoundary caught an error', error, { errorInfo })

    // Report to backend telemetry (Azure App Insights + ELK)
    errorReporting.reportImmediate(error, 'ErrorBoundary', {
      componentStack: errorInfo.componentStack,
    })
    try {
      captureBufferPushError(error, 'ErrorBoundary', {
        componentStack: errorInfo.componentStack,
      })
      void maybeReportAutoBug(error, 'ErrorBoundary')
    } catch {
      /* ignore */
    }

    this.props.onError?.(error, errorInfo)
  }

  // Reset error state when route changes (if location prop is provided)
  componentDidUpdate(prevProps: Props) {
    if (
      this.props.location &&
      prevProps.location &&
      prevProps.location.pathname !== this.props.location.pathname
    ) {
      this.setState({ hasError: false, error: undefined })
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      // 403 → render the access-denied page instead of the generic crash card
      // so users understand the failure is intentional (no admin access, etc.),
      // not a system fault.
      if (isForbiddenError(this.state.error)) {
        return <PermissionDenied />
      }

      const chunkError = this.state.error && isChunkLoadError(this.state.error)
      const handleAction = chunkError ? this.handleReload : this.handleRetry

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: 'var(--surface)',
            borderRadius: '12px',
            border: '1px solid var(--card-border)',
            margin: '20px',
          }}
        >
          <AlertTriangle size={48} style={{ color: 'var(--error)', marginBottom: '16px' }} />
          <h3
            style={{
              margin: '0 0 8px 0',
              color: 'var(--text)',
              fontSize: '18px',
              fontWeight: '600',
            }}
          >
            {chunkError ? 'New version available' : 'Something went wrong'}
          </h3>
          <p
            style={{
              margin: '0 0 24px 0',
              color: 'var(--text-muted)',
              fontSize: '14px',
              maxWidth: '400px',
              lineHeight: '1.5',
            }}
          >
            {chunkError
              ? 'The app was updated. Refresh the page to load the latest version.'
              : 'An unexpected error occurred. You can try refreshing the page or contact support if the problem persists.'}
          </p>
          <button
            onClick={handleAction}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--primary-hover)')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--primary)')}
          >
            <RefreshCw size={16} />
            {chunkError ? 'Refresh page' : 'Try Again'}
          </button>
          {config.env.isDevelopment && this.state.error && (
            <details
              style={{
                marginTop: '24px',
                textAlign: 'left',
                maxWidth: '600px',
                width: '100%',
              }}
            >
              <summary
                style={{
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  fontSize: '12px',
                  marginBottom: '8px',
                }}
              >
                Error Details (Development)
              </summary>
              <pre
                style={{
                  backgroundColor: 'var(--surface-alt)',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  color: 'var(--error)',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  border: '1px solid var(--card-border)',
                }}
              >
                {this.state.error.toString()}
              </pre>
            </details>
          )}
        </div>
      )
    }

    return this.props.children
  }
}

// Export Props type for wrapper
export type ErrorBoundaryProps = Props
